/**
 * Remove chaves com valor `undefined` de um objeto antes de gravar no Firestore.
 *
 * O Firestore rejeita `undefined` com "Unsupported field value: undefined" — a
 * config do projeto usa o `getFirestore` padrão (sem `ignoreUndefinedProperties`).
 * Campos opcionais em TS frequentemente chegam como `undefined`, então o payload
 * passa por aqui antes de `setDoc`/`updateDoc`/`addDoc`.
 *
 * Filtro raso (só o nível de topo) — suficiente pros payloads do app, que não
 * aninham objetos parciais. `FieldValue` (ex.: `serverTimestamp()`) passa batido
 * porque não é `undefined`.
 */
export function stripUndefined<T extends Record<string, unknown>>(
  obj: T,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined),
  );
}

/** Objeto "plano" ({}), não uma instância (Date, Timestamp, FieldValue…). */
function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (typeof v !== 'object' || v === null) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

/**
 * Versão PROFUNDA do `stripUndefined`: remove `undefined` em qualquer nível,
 * recorrendo em objetos planos e arrays. Necessário pra payloads aninhados
 * (ex.: `content.encounterExams.aiFilled` fica `undefined` no preenchimento
 * manual) — o Firestore rejeita `undefined` em QUALQUER profundidade, não só
 * no topo. Preserva `null`, datas, `Timestamp` e sentinelas `FieldValue`
 * (ex.: `serverTimestamp()`) intactos, tratando-os como folhas.
 */
export function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => stripUndefinedDeep(v)) as unknown as T;
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue;
      out[k] = stripUndefinedDeep(v);
    }
    return out as unknown as T;
  }
  return value;
}
